import { Fingerprint, KeyRound, ListChecks, Mail, MessageSquare, Smartphone } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * FactorPickerStep — the screen the user sees when the pipeline runtime
 * pauses at `mfa.picker` (an mfa block configured with `mfa_factor_id:
 * "any"` and the user has 2+ enrolled factors).
 *
 * The molecule is intentionally **headless of the pipeline**. The parent
 * (LoginForm / MfaPage) already knows the available + preferred factors
 * from the pause envelope; this component only renders a labelled
 * radiogroup of cards, captures the choice, and bubbles `onChoose` with
 * the picked `mfa_factor_id`.
 *
 * A11y:
 *   - Container is a `role="radiogroup"` with an `aria-label`.
 *   - Each card is a real `<button role="radio">` with `aria-checked`.
 *   - Arrow Up / Down (and Left / Right) navigate between cards; Home /
 *     End jump to first / last; Enter or Space activates.
 *   - The "Recommended" tag on the preferred entry is `aria-hidden` because
 *     the same emphasis is communicated through `aria-checked`'s initial
 *     value when the parent passes `preferred`.
 *   - Inline error is `role="alert"` + `aria-live="polite"`.
 *
 * No network calls — the parent owns the pipeline step transition.
 */

/**
 * Factor identifiers the picker knows how to label + icon. The string set
 * mirrors `MFAFactorID` in dp-auth-ui's pipeline-pause-types.ts (the wire
 * authority). The DS package can't import from an app, so the union is
 * re-declared here; CI keeps them in sync via the same TypeScript-strict
 * pipeline parsing on the consumer side.
 */
export type FactorPickerBuiltInFactorID =
  | 'password'
  | 'magic_link'
  | 'totp'
  | 'sms_otp'
  | 'email_otp'
  | 'backup_code'
  | 'webauthn_platform'
  | 'webauthn_security_key'

/**
 * Factor identifiers the picker accepts. Built-in factors carry a default
 * icon + the app's humaniser; tenant CUSTOM factors arrive as an opaque
 * `ftype_...` id (or slug on the enroll picker), which is not in the
 * built-in union — hence the `string` widening. The consumer supplies the
 * display `label` (and, if desired, an `icon`) for custom entries; the DS
 * never invents a name or a factor-specific icon for an unknown id.
 */
export type FactorPickerMFAFactorID = FactorPickerBuiltInFactorID | (string & {})

export interface FactorPickerEntry {
  /** Factor identifier — matches the pause envelope's `mfa_factor_id`. */
  mfa_factor_id: FactorPickerMFAFactorID
  /** Display label, e.g. "Authenticator app", "Text message". */
  label: string
  /**
   * Optional icon override. When absent, a default lucide icon is picked
   * from the mfa_factor_id (Smartphone for totp, MessageSquare for sms_otp,
   * Mail for email_otp, Fingerprint for webauthn_platform, KeyRound for
   * webauthn_security_key, ListChecks for backup_code).
   */
  icon?: React.ReactNode
  /** Optional sub-label, e.g. "ending in 3210" or "a***@example.com". */
  hint?: string
}

export interface FactorPickerStepProps {
  /**
   * The list of factors the user can pick from. Order is preserved in the
   * UI; the parent should pre-sort by preference / familiarity.
   */
  available: FactorPickerEntry[]
  /**
   * When provided, the matching entry renders with an emphasized border +
   * "Recommended" tag and starts focused / `aria-checked`. When absent,
   * the first available entry takes the focus + emphasis.
   */
  preferred?: FactorPickerMFAFactorID
  /** Called with the picked `mfa_factor_id` when the user chooses. */
  onChoose: (mfa_factor_id: FactorPickerMFAFactorID) => void | Promise<void>
  /**
   * When provided, renders a tertiary "Skip for now" button below the
   * factor cards. The parent calls the backend resume with
   * `{ action: "skip" }` (or whatever its protocol expects). Used by the
   * `mfa_enroll` enrollment picker when the block was configured
   * with `allow_skip = true`; omit for the verify-path `mfa` picker.
   */
  onSkip?: () => void | Promise<void>
  /**
   * Override the skip button label. Default: "Skip for now".
   */
  skipLabel?: string
  /** Inline error surfaced by the parent (e.g. "Couldn't load factors"). */
  error?: string
  /** Set true while a choice is being processed; locks all cards. */
  busy?: boolean
  /** Override the visible heading. Default: "Choose how to verify your identity". */
  heading?: string
  /** Optional helper text rendered under the heading. */
  helperText?: string
  /**
   * Minimum factors the user must enroll for the gate to pass. Set by the
   * `mfa_enroll` block; absent for the verify-path `mfa` picker.
   * When set with `enrolledCount`, drives the progress hint:
   *   - minFactors = 1: "Choose any one"
   *   - minFactors > 1 and enrolledCount < minFactors: "N of M enrolled"
   * If `helperText` is also provided, it takes precedence.
   */
  minFactors?: number
  /** Non-password MFA factors already enrolled at pause-emission time. */
  enrolledCount?: number
  /** Extra classes for the wrapper. */
  className?: string
}

const DEFAULT_ICON_MAP: Record<FactorPickerBuiltInFactorID, React.ReactNode> = {
  // Lucide icons sized 5 — matches the 5x5 glyph slot in the card.
  password: <Smartphone className="h-5 w-5" />,
  magic_link: <Mail className="h-5 w-5" />,
  totp: <Smartphone className="h-5 w-5" />,
  sms_otp: <MessageSquare className="h-5 w-5" />,
  email_otp: <Mail className="h-5 w-5" />,
  backup_code: <ListChecks className="h-5 w-5" />,
  webauthn_platform: <Fingerprint className="h-5 w-5" />,
  webauthn_security_key: <KeyRound className="h-5 w-5" />,
}

export const FactorPickerStep = React.forwardRef<HTMLDivElement, FactorPickerStepProps>(
  (
    {
      available,
      preferred,
      onChoose,
      onSkip,
      skipLabel = 'Skip for now',
      error,
      busy = false,
      heading = 'Choose how to verify your identity',
      helperText,
      minFactors,
      enrolledCount,
      className,
    },
    ref,
  ) => {
    // The radiogroup's "checked" entry — drives aria-checked + visual
    // emphasis. Initialized to the preferred factor if available; falls
    // back to the first entry.
    const initialChecked = React.useMemo<FactorPickerMFAFactorID | undefined>(() => {
      if (preferred && available.some((e) => e.mfa_factor_id === preferred)) return preferred
      return available[0]?.mfa_factor_id
    }, [available, preferred])

    const [checked, setChecked] = React.useState<FactorPickerMFAFactorID | undefined>(
      initialChecked,
    )

    // Keep `checked` in sync if the parent swaps the available list (e.g.
    // a new pause arrives because the user picked, the picker stays
    // mounted briefly, and the parent re-renders with a different factor
    // set). Same single-strike pattern that other pause molecules use.
    React.useEffect(() => {
      setChecked(initialChecked)
    }, [initialChecked])

    const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([])

    const focusIndex = React.useCallback(
      (next: number) => {
        if (available.length === 0) return
        const wrapped = ((next % available.length) + available.length) % available.length
        const target = buttonRefs.current[wrapped]
        if (target) {
          target.focus()
          setChecked(available[wrapped].mfa_factor_id)
        }
      },
      [available],
    )

    const handleKeyDown = React.useCallback(
      (index: number, e: React.KeyboardEvent<HTMLButtonElement>) => {
        if (busy) return
        switch (e.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            e.preventDefault()
            focusIndex(index + 1)
            break
          case 'ArrowUp':
          case 'ArrowLeft':
            e.preventDefault()
            focusIndex(index - 1)
            break
          case 'Home':
            e.preventDefault()
            focusIndex(0)
            break
          case 'End':
            e.preventDefault()
            focusIndex(available.length - 1)
            break
          default:
            break
        }
      },
      [available.length, busy, focusIndex],
    )

    const handleChoose = React.useCallback(
      async (id: FactorPickerMFAFactorID) => {
        if (busy) return
        setChecked(id)
        await onChoose(id)
      },
      [busy, onChoose],
    )

    const handleSkip = React.useCallback(async () => {
      if (busy || !onSkip) return
      await onSkip()
    }, [busy, onSkip])

    // Progress hint derived from minFactors + enrolledCount. Honours an
    // explicit helperText override -- the parent's copy always wins.
    // For minFactors = 1 the hint is encouragement-shaped ("Choose any
    // one"); for higher minima it shows the count progress.
    const progressHint = React.useMemo<string | undefined>(() => {
      if (helperText) return helperText
      if (minFactors === undefined || minFactors <= 0) return undefined
      if (minFactors === 1) return 'Choose any one to continue.'
      const have = Math.min(enrolledCount ?? 0, minFactors)
      return `${have} of ${minFactors} enrolled. Add one more to continue.`
    }, [helperText, minFactors, enrolledCount])

    return (
      <div ref={ref} className={cn('flex flex-col gap-4', className)}>
        <div className="flex flex-col gap-1 text-center">
          <h2 className="text-heading-sm font-semibold text-ia-text">{heading}</h2>
          {progressHint ? (
            <p className="text-body-sm text-ia-text-secondary">{progressHint}</p>
          ) : null}
        </div>

        <div role="radiogroup" aria-label="verification method" className="flex flex-col gap-2">
          {available.map((entry, index) => {
            const isChecked = checked === entry.mfa_factor_id
            const isPreferred = preferred === entry.mfa_factor_id
            // Built-in ids map to a default lucide icon; custom ("ftype_*")
            // ids have no default (undefined -> no glyph) unless the caller
            // passed an explicit icon. The DS never guesses an icon for an
            // unknown factor.
            const icon =
              entry.icon ??
              DEFAULT_ICON_MAP[entry.mfa_factor_id as FactorPickerBuiltInFactorID]
            return (
              // biome-ignore lint/a11y/useSemanticElements: a native <input type="radio"> can't host the icon + label + hint + Recommended-tag composition; the WAI-ARIA radiogroup pattern (role=radio + aria-checked + roving tabIndex + arrow-key handler) is the documented alternative and is implemented in full above.
              <button
                key={entry.mfa_factor_id}
                ref={(el) => {
                  buttonRefs.current[index] = el
                }}
                type="button"
                role="radio"
                aria-checked={isChecked}
                // Only the checked card lands in the tab order; arrows move
                // focus + check together (WAI-ARIA radiogroup pattern).
                tabIndex={isChecked ? 0 : -1}
                disabled={busy}
                onClick={() => handleChoose(entry.mfa_factor_id)}
                onKeyDown={(e) => {
                  // Enter / Space activate (browser fires click for Space
                  // on <button>, but we also want Enter to commit even
                  // when the card is already checked).
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    void handleChoose(entry.mfa_factor_id)
                    return
                  }
                  handleKeyDown(index, e)
                }}
                className={cn(
                  'group flex w-full items-center gap-3 rounded-md border bg-ia-input-bg p-3 text-left',
                  'transition-[border-color,background-color,box-shadow] duration-150',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
                  'disabled:cursor-not-allowed disabled:opacity-50',
                  isChecked
                    ? 'border-primary bg-ia-elevated'
                    : isPreferred
                      ? 'border-ia-border-strong'
                      : 'border-ia-border hover:border-ia-border-strong',
                )}
              >
                <span
                  className={cn(
                    'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md',
                    isChecked ? 'bg-primary/10 text-primary' : 'bg-surface text-ia-text-secondary',
                  )}
                  aria-hidden
                >
                  {icon}
                </span>
                <span className="flex flex-1 flex-col">
                  <span className="text-body-md font-medium text-ia-text">{entry.label}</span>
                  {entry.hint ? (
                    <span className="text-body-sm text-ia-text-secondary">{entry.hint}</span>
                  ) : null}
                </span>
                {isPreferred ? (
                  <span
                    // aria-hidden because the radiogroup's aria-checked
                    // already conveys the same "preferred / selected"
                    // emphasis to AT users.
                    aria-hidden
                    className="ml-auto rounded-pill bg-ia-green-muted px-2 py-0.5 text-body-sm font-medium text-ia-green"
                  >
                    Recommended
                  </span>
                ) : null}
              </button>
            )
          })}
        </div>

        {onSkip ? (
          // Tertiary action: only rendered when the parent passes onSkip
          // (drives the `can_skip` field on the pause envelope). Plain
          // text-button styling so it doesn't compete visually with the
          // factor cards above; the cards are the encouraged path.
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              void handleSkip()
            }}
            className={cn(
              'mx-auto rounded-md px-3 py-1.5 text-body-sm font-medium text-ia-text-secondary',
              'transition-colors duration-150 hover:text-ia-text',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            {skipLabel}
          </button>
        ) : null}

        {/* Reserved error row so the layout doesn't jump when the parent
            sets / clears error. role=alert is only present when there
            actually IS an error -- a placeholder string with role=alert
            leaks the word "placeholder" into assistive tech + test
            snapshots. */}
        <div
          {...(error ? { role: 'alert', 'aria-live': 'polite' } : { 'aria-hidden': true })}
          className={cn(
            'min-h-5 text-center text-body-sm',
            error ? 'text-ia-error' : 'text-transparent',
          )}
        >
          {error || ' '}
        </div>
      </div>
    )
  },
)
FactorPickerStep.displayName = 'FactorPickerStep'
